import { Aspiration, AspirationCategory, AspirationPriority, AspirationStatus } from '../types/aspirations';

// Generate unique IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

class AspirationsService {
  private aspirations: Aspiration[] = [];

  constructor() {
    // Load data from localStorage if available
    this.loadFromLocalStorage();
    // Ensure initial data has unique priorities
    this.normalizeAllPriorities();
  }

  private loadFromLocalStorage(): void {
    const stored = localStorage.getItem('godmode_aspirations');
    if (stored) {
      this.aspirations = JSON.parse(stored);
      console.log('📋 AspirationsService: Loaded from localStorage:', this.aspirations.length, 'aspirations');
    }
  }

  private saveToLocalStorage(): void {
    localStorage.setItem('godmode_aspirations', JSON.stringify(this.aspirations));
  }


  // Helper method to ensure all priorities are unique and sequential
  private normalizeAllPriorities(): void {
    console.log('🔧 Normalizing all priorities to ensure uniqueness');
    
    // Sort by current priority, then by creation date as tiebreaker
    const sorted = [...this.aspirations].sort((a, b) => {
      if (a.priority === b.priority) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return a.priority - b.priority;
    });

    // Reassign sequential priorities
    sorted.forEach((aspiration, index) => {
      const newPriority = index + 1;
      const existingAspiration = this.aspirations.find(a => a.id === aspiration.id);
      if (existingAspiration) {
        existingAspiration.priority = newPriority;
        existingAspiration.updatedAt = new Date();
      }
    });

    console.log('✅ Priorities normalized:', 
      this.aspirations.map(a => ({ id: a.id, title: a.title, priority: a.priority }))
    );
    
    // Save to localStorage after normalization
    this.saveToLocalStorage();
  }

  // Helper method to shift priorities when inserting at a specific position
  private shiftPrioritiesFromPosition(fromPriority: number, shift: number = 1): void {
    console.log(`🔄 Shifting priorities from ${fromPriority} by ${shift}`);
    
    this.aspirations.forEach(aspiration => {
      if (aspiration.priority >= fromPriority) {
        const oldPriority = aspiration.priority;
        aspiration.priority += shift;
        aspiration.updatedAt = new Date();
        console.log(`  📈 Shifted "${aspiration.title}": ${oldPriority} → ${aspiration.priority}`);
      }
    });
  }

  // Synchronous method for internal use
  getAspirationsSync(): Aspiration[] {
    return [...this.aspirations];
  }

  async getAspirations(): Promise<Aspiration[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...this.aspirations];
  }

  async getAspirationById(id: string): Promise<Aspiration | null> {
    await new Promise(resolve => setTimeout(resolve, 50));
    return this.aspirations.find(aspiration => aspiration.id === id) || null;
  }

  async createAspiration(aspirationData: Omit<Aspiration, 'id' | 'createdAt' | 'updatedAt'>): Promise<Aspiration> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    console.log('🆕 Creating new aspiration with priority:', aspirationData.priority);
    
    const targetPriority = aspirationData.priority;
    
    // Check if the target priority is already taken
    const existingAtPriority = this.aspirations.find(a => a.priority === targetPriority);
    
    if (existingAtPriority) {
      console.log(`⚠️ Priority ${targetPriority} already exists, shifting priorities down`);
      // Shift all aspirations at or below this priority down by 1
      this.shiftPrioritiesFromPosition(targetPriority, 1);
    }
    
    // Create new aspiration with the requested priority
    const newAspiration: Aspiration = {
      ...aspirationData,
      id: generateId(),
      priority: targetPriority,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    // Add new aspiration to the list
    this.aspirations.push(newAspiration);
    
    // Ensure all priorities are still unique and sequential
    this.normalizeAllPriorities();
    
    console.log(`✅ Created aspiration "${newAspiration.title}" with priority ${newAspiration.priority}`);
    
    // Save to localStorage
    this.saveToLocalStorage();
    
    return newAspiration;
  }

  async updateAspiration(id: string, updates: Partial<Aspiration>): Promise<Aspiration | null> {
    await new Promise(resolve => setTimeout(resolve, 150));
    
    const index = this.aspirations.findIndex(aspiration => aspiration.id === id);
    if (index === -1) return null;
    
    this.aspirations[index] = {
      ...this.aspirations[index],
      ...updates,
      updatedAt: new Date(),
    };
    
    // Save to localStorage
    this.saveToLocalStorage();
    
    return this.aspirations[index];
  }

  async deleteAspiration(id: string): Promise<boolean> {
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const index = this.aspirations.findIndex(aspiration => aspiration.id === id);
    if (index === -1) return false;
    
    this.aspirations.splice(index, 1);
    
    // Save to localStorage
    this.saveToLocalStorage();
    
    return true;
  }

  async reorderAspirations(reorderedAspirations: Aspiration[]): Promise<Aspiration[]> {
    console.log('🔧 Service: Starting reorderAspirations');
    console.log('📝 Service: Received reordered aspirations:', 
      reorderedAspirations.map(a => ({ id: a.id, title: a.title, priority: a.priority }))
    );
    console.log('📚 Service: Current aspirations before reorder:', 
      this.aspirations.map(a => ({ id: a.id, title: a.title, priority: a.priority }))
    );
    
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Find the aspiration that changed priority
    const changedAspiration = reorderedAspirations.find(newAsp => {
      const oldAsp = this.aspirations.find(a => a.id === newAsp.id);
      return oldAsp && oldAsp.priority !== newAsp.priority;
    });
    
    if (!changedAspiration) {
      console.log('🤷 Service: No priority changes detected');
      // If reorderedAspirations is empty, return empty array
      if (reorderedAspirations.length === 0) {
        return [];
      }
      return [...this.aspirations];
    }
    
    const oldAspiration = this.aspirations.find(a => a.id === changedAspiration.id)!;
    const oldPriority = oldAspiration.priority;
    const newPriority = changedAspiration.priority;
    
    console.log(`🎯 Service: Priority change detected for "${changedAspiration.title}": ${oldPriority} → ${newPriority}`);
    
    // First, update the dragged item's priority
    const targetAspiration = this.aspirations.find(a => a.id === changedAspiration.id);
    if (targetAspiration) {
      targetAspiration.priority = newPriority;
      targetAspiration.updatedAt = new Date();
    }
    
    // Handle priority shifts for other aspirations
    if (oldPriority < newPriority) {
      // Item moved down in priority (number increased)
      // Shift up (decrease priority numbers) for items between old and new positions
      this.aspirations.forEach(aspiration => {
        if (aspiration.id !== changedAspiration.id && 
            aspiration.priority > oldPriority && 
            aspiration.priority <= newPriority) {
          console.log(`📈 Service: Shifting "${aspiration.title}" up: ${aspiration.priority} → ${aspiration.priority - 1}`);
          aspiration.priority -= 1;
          aspiration.updatedAt = new Date();
        }
      });
    } else {
      // Item moved up in priority (number decreased)  
      // Shift down (increase priority numbers) for items between new and old positions
      this.aspirations.forEach(aspiration => {
        if (aspiration.id !== changedAspiration.id && 
            aspiration.priority >= newPriority && 
            aspiration.priority < oldPriority) {
          console.log(`📉 Service: Shifting "${aspiration.title}" down: ${aspiration.priority} → ${aspiration.priority + 1}`);
          aspiration.priority += 1;
          aspiration.updatedAt = new Date();
        }
      });
    }
    
    // Ensure no duplicate priorities remain
    this.normalizeAllPriorities();
    
    console.log('✨ Service: Final aspirations after priority shifts and normalization:', 
      this.aspirations.map(a => ({ id: a.id, title: a.title, priority: a.priority }))
    );
    
    // Save to localStorage
    this.saveToLocalStorage();
    
    return [...this.aspirations];
  }

  getCategories(): AspirationCategory[] {
    return ['Career', 'Financial', 'Health', 'Education', 'Personal', 'Family', 'Travel', 'Social', 'Spiritual'];
  }

  getPriorities(): AspirationPriority[] {
    // Return a sample range of priorities for forms/filters
    return [1, 2, 3, 4, 5];
  }

  getStatuses(): AspirationStatus[] {
    return ['not_started', 'in_progress', 'completed'];
  }
}

export const aspirationsService = new AspirationsService();